import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import { Shield } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Terms and Conditions | George Dives"
        description="Terms and Conditions for George Dives diving services, courses, and bookings. Read our terms before using our services."
        keywords={['terms and conditions', 'diving terms', 'booking terms', 'service agreement']}
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-slate-700 to-slate-900 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 mb-4">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-semibold">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Terms and Conditions
          </h1>
          <p className="text-lg text-gray-300">
            Last updated: December 10, 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-700 mb-4">
              Welcome to George Dives. These Terms and Conditions ("Terms") govern your use of our website and services. By accessing or using our services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use our services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Services Provided</h2>
            <p className="text-slate-700 mb-4">
              George Dives provides professional diving instruction, PADI certification courses, fun diving packages, and dive travel services. All services are provided in partnership with certified dive centers worldwide.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Bookings and Reservations</h2>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.1 Booking Process</h3>
            <p className="text-slate-700 mb-4">
              Bookings can be made through our website contact form, email, or WhatsApp. All bookings are subject to availability and confirmation.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.2 Payment Terms</h3>
            <p className="text-slate-700 mb-4">
              Payment terms, deposit requirements, and cancellation policies will be communicated upon booking confirmation. Payment methods and schedules may vary depending on the dive center and location.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">3.3 Cancellation Policy</h3>
            <p className="text-slate-700 mb-4">
              Cancellation policies vary by dive center and package. Specific cancellation terms will be provided at the time of booking. Generally, cancellations made more than 14 days before the scheduled date may receive a partial or full refund, subject to dive center policies.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Participant Requirements</h2>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.1 Medical Fitness</h3>
            <p className="text-slate-700 mb-4">
              All participants must complete a medical questionnaire before diving. Certain medical conditions may require a physician's clearance. Participants must be in good physical and mental health suitable for diving activities.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.2 Age Requirements</h3>
            <p className="text-slate-700 mb-4">
              Minimum age requirements apply to all courses and dives as per PADI standards. Participants under 18 must have parental/guardian consent.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">4.3 Swimming Ability</h3>
            <p className="text-slate-700 mb-4">
              Participants must be able to swim and be comfortable in water. Specific swimming proficiency requirements apply to certification courses.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Safety and Liability</h2>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.1 Inherent Risks</h3>
            <p className="text-slate-700 mb-4">
              Scuba diving involves inherent risks. Participants acknowledge that diving activities carry risks of injury or death, and they voluntarily assume these risks.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.2 Liability Waiver</h3>
            <p className="text-slate-700 mb-4">
              Participants will be required to sign liability release and assumption of risk forms before participating in any diving activities. These forms are binding legal documents.
            </p>
            <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-3">5.3 Insurance</h3>
            <p className="text-slate-700 mb-4">
              We strongly recommend that all participants obtain appropriate dive insurance and travel insurance. George Dives and partner dive centers are not responsible for personal injuries, accidents, or loss of personal property.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Course Certification</h2>
            <p className="text-slate-700 mb-4">
              All courses follow PADI standards and procedures. Certification is dependent on successful completion of all course requirements, including knowledge development, confined water training, and open water dives. Instructors reserve the right to withhold certification if performance standards are not met.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Equipment</h2>
            <p className="text-slate-700 mb-4">
              Diving equipment is typically provided by partner dive centers as part of the package. Participants are responsible for inspecting equipment before use and reporting any concerns to the instructor or dive center staff. Personal equipment may be used subject to inspection and approval.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Weather and Environmental Conditions</h2>
            <p className="text-slate-700 mb-4">
              Diving activities are weather-dependent. The instructor and dive center reserve the right to postpone, cancel, or modify activities due to unsafe weather or water conditions. Safety is always the top priority.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Conduct and Behavior</h2>
            <p className="text-slate-700 mb-4">
              Participants are expected to behave responsibly and follow all instructions from instructors and dive center staff. George Dives and partner dive centers reserve the right to terminate services to any participant who displays dangerous, disruptive, or inappropriate behavior without refund.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Photography and Media</h2>
            <p className="text-slate-700 mb-4">
              Photos and videos may be taken during courses and dives for educational, promotional, or social media purposes. By participating, you consent to the use of images and videos featuring you unless you explicitly opt out in writing.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. Privacy and Data Protection</h2>
            <p className="text-slate-700 mb-4">
              Your personal information will be handled in accordance with our Privacy Policy. We collect and use personal data only as necessary to provide our services and communicate with you.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">12. Intellectual Property</h2>
            <p className="text-slate-700 mb-4">
              All content on this website, including text, images, logos, and graphics, is the property of George Dives or licensed partners. Unauthorized use, reproduction, or distribution is prohibited.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">13. Third-Party Services</h2>
            <p className="text-slate-700 mb-4">
              We partner with independent dive centers worldwide. While we carefully select our partners, George Dives is not responsible for the actions, services, or policies of third-party dive centers. Participants enter into separate agreements with dive centers for certain services.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">14. Changes to Terms</h2>
            <p className="text-slate-700 mb-4">
              We reserve the right to modify these Terms at any time. Updated Terms will be posted on this page with a revised "Last Updated" date. Continued use of our services after changes constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">15. Governing Law</h2>
            <p className="text-slate-700 mb-4">
              These Terms are governed by and construed in accordance with the laws of Greece. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of Greece.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">16. Contact Information</h2>
            <p className="text-slate-700 mb-4">
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <ul className="list-disc list-inside text-slate-700 mb-4">
              <li>Email: info@georgedives.com</li>
              <li>Phone/WhatsApp: +30 698 794 6278</li>
              <li>Website: https://georgedives.com</li>
            </ul>

            <div className="mt-12 p-6 bg-cyan-50 border-l-4 border-cyan-500 rounded">
              <p className="text-sm text-slate-700">
                <strong>Important:</strong> By using George Dives services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} George Dives. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;